import DOMPurify from 'dompurify';
import React, { useState, useRef, useMemo } from 'react';
import { AnswerType, Question } from '../services/QuestionStore';
import { useTranslation } from 'react-i18next';

import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Slider } from 'primereact/slider';
import './ShowQuestion.css';

const Answers: React.FC<{ question: Question, onAnswer: (answer: string) => void, readOnly?: boolean }> = ({ question: { type, nextButtonText, minLabel, maxLabel, answers, last }, onAnswer, readOnly }) => {
    const { t } = useTranslation();
    const [text, setText] = useState<string>('');
    const [value, setValue] = useState<number>(5);
    const sliderRef = useRef<Slider>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const handleAnswer = (e: HTMLButtonElement, answer: string) => {
        e.blur();
        onAnswer(answer);
        setText('');
    };

    const nextButton = nextButtonText || 'Következő kérdés';
    switch (type) {
        case AnswerType.IQ:
            return answers ?
                (<>
                    <div style={{ width: '100%', textAlign: 'center' }}>
                        {answers?.map((answer, index) => (
                            <div style={{ marginLeft: "0.5em", minWidth: '16%', display: 'inline-block' }}>
                                {answer.text && <Button style={{ marginTop: '0.5rem', width: '100%', textAlign: 'center', display: 'inline-block' }} key={answer.id} onClick={(e) => handleAnswer(e.currentTarget, "" + (index + 1))} disabled={readOnly}>{answer.text}</Button>}
                                {answer.imageUrl && <Button onClick={(e) => handleAnswer(e.currentTarget, "" + (index + 1))} disabled={readOnly}><img key={answer.id} className="answer-image" src={answer.imageUrl} alt="Answer" /></Button>}
                            </div>
                        ))}
                    </div>
                </>)
                :
                (<>
                    <div style={{ width: '100%', textAlign: 'center' }}>
                        {[1, 2, 3, 4, 5].map((_, index) => (
                            <Button key={index}
                                onClick={(e) => handleAnswer(e.currentTarget, "" + (index + 1))}
                                style={{ width: '4em', height: '3em', marginRight: '1em', marginLeft: '1em' }}
                                disabled={readOnly}>
                                <h3 style={{ width: '100%', textAlign: 'center' }}>{String.fromCharCode('a'.charCodeAt(0) + index)}</h3>
                            </Button>
                        ))}
                    </div>
                </>);
        case AnswerType.Szemelyiseg:
            return (
                <div style={{ textAlign: 'center' }}>
                    <div className="p-fluid grid" >
                        {
                            [
                                ["1", t('answers.yes')],
                                ["2", t('answers.ratherYes')],
                                ["3", t('answers.ratherNo')],
                                ["4", t('answers.no')]
                            ].map((answer) => (
                                <div key={answer[0]} className='col-12 md:col-3'>
                                    <Button key={answer[0]}
                                        onClick={(e) => handleAnswer(e.currentTarget, answer[0])}
                                        style={{ width: '10em', height: '3em'}}
                                        disabled={readOnly}>
                                        <div style={{ width: '100%', textAlign: 'center' }}>{answer[1]}</div>
                                    </Button>
                                </div>
                            ))}
                    </div>
                </div>
            );
        case AnswerType.SzabadSzoveg:
            return (<>
                <InputTextarea rows={4} style={{ width: "100%" }} value={text} onChange={(e) => setText(e.target.value)} readOnly={readOnly} />
                <Button onClick={(e) => handleAnswer(e.currentTarget, text)} disabled={readOnly}>{nextButton}</Button>
            </>);
        case AnswerType.Sor:
            return (<>
                <InputText style={{ width: "100%", marginBottom: "1em", marginTop: "-1em" }} value={text} onChange={(e) => setText(e.target.value)} readOnly={readOnly} />
                <Button onClick={(e) => handleAnswer(e.currentTarget, text)} disabled={readOnly || "" === text}>{nextButton}</Button>
            </>);
        case AnswerType.Skala:
            const mouseDown = () => {
                setIsDragging(true);
            };

            const mouseUp = (e: React.MouseEvent) => {
                if (isDragging) {
                    updateSliderValue(e);
                    setIsDragging(false);
                }
            };

            const mouseMove = (e: React.MouseEvent) => {
                if (isDragging) {
                    updateSliderValue(e);
                }
            };

            const updateSliderValue = (e: React.MouseEvent) => {
                const rect = sliderRef.current?.getElement().getBoundingClientRect();
                if (rect) {
                    const newValue = Math.round(((e.clientX - rect.left) / rect.width) * 9) + 1;
                    setValue(newValue);
                }
                e.stopPropagation();
            };

            return (<>
                <div style={{ marginTop: 4, marginBottom: 4 }}
                    onMouseDownCapture={mouseDown}
                    onMouseUpCapture={mouseUp}
                    onMouseMoveCapture={mouseMove}>
                    <Slider ref={sliderRef} value={value} min={1} max={10} onChange={(e) => setValue(e.value as number)} disabled={readOnly} />
                    {(minLabel || maxLabel) &&
                        <div className="flex justify-content-between" style={{ marginTop: 0 }}>
                            <span>{minLabel}</span>
                            <span style={{ textAlign: 'right' }}>{maxLabel}</span>
                        </div>
                    }
                </div>
                Kérjük indokolja meg a választ:
                <InputTextarea rows={4} style={{ width: "100%" }} value={text} onChange={(e) => setText(e.target.value)} readOnly={readOnly} />
                <Button onClick={(e) => handleAnswer(e.currentTarget, value + "\n" + text)} disabled={readOnly}>{nextButton}</Button>
            </>);
        case AnswerType.Szoveg:
            const next = last || nextButtonText === '';

            return next ? <></> : <Button onClick={(e) => handleAnswer(e.currentTarget, 'Submit')} disabled={readOnly}>{nextButton}</Button>;
        // default:
        //    return null;
    }
};


interface ShowQuestionProps {
    question: Question;
    onAnswer: (answer: string) => void;
    readOnly?: boolean;
}

export const ShowQuestion: React.FC<ShowQuestionProps> = ({ question, onAnswer, readOnly }) => {
    const sanitizedText = useMemo(() => DOMPurify.sanitize(question.text), [question.text]);
    const prefix = question.type === AnswerType.IQ && question.orderNumber ? question.orderNumber + '. ' : '';
    return (
        <>
            <div style={{ margin: "1rem", minHeight: "2.5rem" }}>
                <big dangerouslySetInnerHTML={{ __html: prefix + sanitizedText }} />
                {question.imageUrl && <div style={{ marginTop: "0.5rem", textAlign: 'center', width: '100%' }}><img src={question.imageUrl} alt="Question" className="question-image" /></div>}
            </div>
            <Answers question={question} onAnswer={onAnswer} readOnly={readOnly} />
        </>
    );
};
